<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue'
import * as apisPortal from '/@src/client-portal/api'
import { useField, useFormValues } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { useNotyf } from '/@src/composable/useNotyf'
import {
  StrukturovanaPodmienkaSpecRef,
  ZoppmPodmienkaSpecRef,
  ZoppmPodmienkaSpecRefTypEnum,
} from '/@src/client-public'
import FileDownload from 'js-file-download'
import { Dokument, ZoPPMPriloha } from '/@src/client-portal'
import { MAX_FILE_SIZE_MB } from '/@src/constants/index'
import { checkFiles } from '/@src/utils/checkFiles'
import { Notyf } from 'notyf'

export interface PodmienkyPoskytnutiaProps {
  idx: number
  arrayPosition: number
  name: string
  nazov: string
  strukturovanaPodmienkaSpecRef?: StrukturovanaPodmienkaSpecRef
  specRefName: ZoppmPodmienkaSpecRefTypEnum
  zoppmPodmienkaSpecRef: ZoppmPodmienkaSpecRef
}

const props = defineProps<PodmienkyPoskytnutiaProps>()

const { t } = useI18n()
const notif = useNotyf()
const { setValue: setPriloha } = useField(`${props.name}.prilohy[${props.idx}]`)
const api = new apisPortal.ZoppmDraftApi()
const kodIrz = inject<Ref<string>>('kodIrz')
const isReadOnly = inject<boolean>('isReadOnly')
const formValues = useFormValues()
const loadingFile = ref<boolean>(false)

const prilohy = computed(
  () => formValues.value?.podmienkyPoskytnutiaPrispevku?.[props.arrayPosition]?.prilohy
)

/**
 * Index konkrétnej prílohy treba vždy prepočítať, pretože ak sa počet a teda aj poradie
 * priloh zmení, súbory preskakujú do prilohy predchádzajúcej v poradí
 */
const idx = computed(() =>
  prilohy.value?.findIndex((priloha: ZoPPMPriloha) => {
    const refId = priloha?.priloha?.lokalnaReferencia?.refId
    return (
      refId === props?.strukturovanaPodmienkaSpecRef?.zoppmStruktPodmienkaSpecId ||
      refId === props?.zoppmPodmienkaSpecRef?.zoppmPodmienkaSpecId
    )
  })
)

const dokumenty = computed(() => {
  return prilohy.value?.[idx.value]?.priloha?.dokumenty || []
})

const filesList = computed(() =>
  dokumenty.value?.map((value) => {
    return {
      key: `${value?.nazov}.${value?.format}`,
      value: '',
      id: value?.dokumentRef?.identifikatorUlozisko,
    }
  })
)

async function uploadFile(file: File[]) {
  loadingFile.value = true
  const checkedFiles = checkFiles(file)

  const promises: any[] = []
  const dokumentyTemp: Dokument[] = dokumenty.value || []
  //Pouzivatel moze nahrat aj viacero suborov, tak to prebehneme
  if (kodIrz?.value) {
    try {
      Array.from(checkedFiles).forEach((checkedFiles) => {
        promises.push(
          api.storeDokumentForZoppmDraftZoppmDraft(kodIrz.value, checkedFiles)
        )
      })

      await Promise.all(promises)
        .then((responses) => {
          responses.forEach(({ data }) => {
            notif.success(`Súbor ${data?.nazov}.${data?.format} bol úspešne nahraný`)

            dokumentyTemp.push({
              nazov: data?.nazov,
              format: data?.format,
              dokumentRef: data?.ref,
            })
          })
        })
        .catch((error) => {
          if (error?.response?.data?.exception) {
            notif.error(error.response.data.exception)
          }
        })

      /**
       * Prílohu treba vždy nastaviť, pretože `useZoppm` ju premazáva ak nemá žiadne dokumenty
       */
      setPriloha({
        priloha: {
          poradie: props.idx + 1,
          nazov: `Príloha podmienky: ${props.nazov || props.specRefName}`,
          lokalnaReferencia: {
            refSekcia: props.specRefName,
            refId:
              props?.strukturovanaPodmienkaSpecRef?.zoppmStruktPodmienkaSpecId ||
              props?.zoppmPodmienkaSpecRef?.zoppmPodmienkaSpecId,
          },
          dokumenty: dokumentyTemp,
        },
      })
    } catch (error) {
      notif.error('Súbor sa nepodarilo nahrať, chyba siete.')
      loadingFile.value = false
    }
  }
  loadingFile.value = false
}

async function handleFileRemove(identifikatorUlozisko: string, index: number) {
  const verziaUlozisko = dokumenty.value?.find(
    (file: Dokument) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
  )?.dokumentRef?.verziaUlozisko

  if (kodIrz?.value) {
    try {
      await api
        .deleteContentOfDokumentForZoppmDraftZoppmDraft(
          kodIrz.value,
          identifikatorUlozisko,
          verziaUlozisko
        )
        .then(() => {
          if (dokumenty.value?.length === 0) {
            setPriloha(null)
          } else {
            /**
             * Filtrujeme dokument, ktorý chceme odmazať
             * a updatneme prilohu
             */
            const filteredDokumenty = dokumenty.value.filter(
              (_: any, key: number) => key !== index
            )

            setPriloha({
              priloha: {
                poradie: props.idx + 1,
                nazov: `Príloha podmienky: ${props.nazov || props.specRefName}`,
                lokalnaReferencia: {
                  refSekcia: props.specRefName,
                  refId:
                    props?.strukturovanaPodmienkaSpecRef?.zoppmStruktPodmienkaSpecId ||
                    props?.zoppmPodmienkaSpecRef?.zoppmPodmienkaSpecId,
                },
                dokumenty: filteredDokumenty,
              },
            })
          }

          notif.success('Súbor zmazaný')
        })
    } catch {
      notif.error('Nastala chyba')
    }
  }
}

async function getDokument(identifikatorUlozisko: string) {
  const verziaUlozisko = dokumenty.value?.find(
    (file: Dokument) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
  )?.dokumentRef?.verziaUlozisko

  if (kodIrz?.value) {
    try {
      await api
        .fetchContentOfDokumentForZoppmDraftZoppmDraft(
          kodIrz.value,
          identifikatorUlozisko,
          verziaUlozisko,
          undefined,
          undefined,
          {
            responseType: 'blob',
          }
        )
        .then((response) => {
          FileDownload(response.data, response.headers.filename)
        })
    } catch {
      notif.error('Nastala chyba')
    }
  }
}
</script>
<template>
  <template v-if="!isReadOnly && filesList?.length">
    <IDSKHeading as="h4" variant="s" class="mb-0">
      {{ t('globalne.nahraneSubory') }}
    </IDSKHeading>
    <IDSKSummaryList :data="filesList" action-text="Odstrániť" @action="handleFileRemove">
      <template #link="{ value }">
        <IDSKLink href="#" no-visited-state @click.prevent="() => getDokument(value.id)">
          {{ value.key }}
        </IDSKLink>
      </template>
    </IDSKSummaryList>
  </template>

  <loading-spinner v-if="loadingFile" />

  <IDSKDragAndDrop
    :show-defaul-summary-list="isReadOnly ? true : false"
    :label="t('zoppm.step2.nahrajteSubor')"
    :hint="t('zoppm.step2.vyberteSubor') + ` (max. ${MAX_FILE_SIZE_MB}MB)`"
    @added-files="(files) => !isReadOnly && uploadFile(files)"
  />
</template>
